import React, { useEffect, useRef, useState } from "react";
import { COLLECTIONS } from "@/lib/collections";
import { toast } from "sonner";
import { X, Upload, Trash2, Plus, QrCode, Download, Loader2 } from "lucide-react";
import api, { API, CATEGORIES, errMsg, formatARS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = {
  name: "", design_code: "", edition_name: "", edition_total: "", category: "denim", collection: "",
  vip: false, concept: "", interventions: "", materials: "", care: "", shipping_info: "",
  color: "", price: "", sizes: [{ label: "1", measurements: "" }], images: [], video: null, status: "draft",
};

const field = "mt-1.5 bg-[#F4F4F3] border-[#C4C7CD] text-[#171719]";
const lbl = "dossier-label text-[#565A61]";

export default function ProductEditor({ product, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [wlVersion, setWlVersion] = useState(0);
  const fileRef = useRef();
  const videoRef = useRef();
  const guideRef = useRef();
  const token = localStorage.getItem("archive_token");

  useEffect(() => {
    if (product) setF({ ...EMPTY, ...product, edition_total: product.edition_total ?? "", price: product.price ?? "" });
  }, [product]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (file, isVideo) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isVideo) set("video", data.url);
      else set("images", [...f.images, data.url]);
      toast.success("Archivo subido");
    } catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };

  const save = async () => {
    if (!f.name || !f.design_code) return toast.error("Nombre y código de diseño son obligatorios");
    setSaving(true);
    const payload = {
      ...f,
      edition_total: f.edition_total === "" ? null : Number(f.edition_total),
      price: f.price === "" ? null : Number(f.price),
      sizes: f.sizes.filter((s) => s.label),
    };
    try {
      let saved;
      if (product?.id) saved = (await api.put(`/admin/products/${product.id}`, payload)).data;
      else saved = (await api.post("/admin/products", payload)).data;
      toast.success("Pieza guardada");
      onSaved(saved);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="product-editor">
      <div className="min-h-screen flex items-start justify-center p-4 py-10">
        <div className="bg-[#F4F4F3] border border-[#C4C7CD] w-full max-w-3xl">
          <div className="flex items-center justify-between p-5 border-b border-[#C9CBD1] sticky top-0 bg-[#F4F4F3] z-10">
            <h2 className="font-display font-bold uppercase tracking-tight text-xl text-[#171719]">{product?.id ? "Editar pieza" : "Nueva pieza"}</h2>
            <button data-testid="editor-close" onClick={onClose} className="text-[#565A61] hover:text-[#171719]"><X size={20} /></button>
          </div>

          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className={lbl}>Nombre de la pieza</Label><Input data-testid="pe-name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><Label className={lbl}>Código de diseño</Label><Input data-testid="pe-code" className={field} value={f.design_code} onChange={(e) => set("design_code", e.target.value)} placeholder="DNM01" /></div>
              <div><Label className={lbl}>Edición</Label><Input className={field} value={f.edition_name} onChange={(e) => set("edition_name", e.target.value)} placeholder="Liberación I" /></div>
              <div>
                <Label className={lbl}>Categoría</Label>
                <select data-testid="pe-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={`${field} w-full h-10 px-3 rounded-md border`}>
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className={lbl}>Colección (solapa de la web)</Label>
                <select data-testid="pe-collection" value={f.collection} onChange={(e) => set("collection", e.target.value)} className={`${field} w-full h-10 px-3 rounded-md border`}>
                  <option value="">Sin asignar</option>
                  {COLLECTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div><Label className={lbl}>Color</Label><Input className={field} value={f.color} onChange={(e) => set("color", e.target.value)} /></div>
              <div><Label className={lbl}>Precio ARS (vacío = sin precio)</Label><Input data-testid="pe-price" type="number" className={field} value={f.price} onChange={(e) => set("price", e.target.value)} /></div>
              <div><Label className={lbl}>Total de la edición</Label><Input type="number" className={field} value={f.edition_total} onChange={(e) => set("edition_total", e.target.value)} /></div>
              <div className="flex items-center gap-3 pt-6">
                <input data-testid="pe-vip" id="vip" type="checkbox" checked={f.vip} onChange={(e) => set("vip", e.target.checked)} className="w-4 h-4 accent-[#171719]" />
                <Label htmlFor="vip" className="text-[#565A61] text-sm">Sección VIP Camila Guerra</Label>
              </div>
              <div>
                <Label className={lbl}>Estado</Label>
                <select data-testid="pe-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={`${field} w-full h-10 px-3 rounded-md border`}>
                  <option value="draft">Borrador</option>
                  <option value="published">Publicada</option>
                  <option value="archived">Archivo histórico</option>
                </select>
              </div>
            </div>

            {[["concept", "Concepto"], ["interventions", "Intervenciones"], ["materials", "Materiales y avíos"], ["care", "Cuidados"], ["shipping_info", "Información de envío"]].map(([k, l]) => (
              <div key={k}><Label className={lbl}>{l}</Label><Textarea className={field} rows={2} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
            ))}

            {/* Talles: medidas + descripción + guía de medición */}
            <div>
              <Label className={lbl}>Talles · medidas, descripción y guía de medición</Label>
              <p className="text-[11px] text-[#858A91] mt-1">Esto se muestra en la ficha cuando la persona elige un talle. En la descripción podés contar cómo queda, a quién le va bien y cómo medir en casa.</p>
              <div className="flex items-center gap-3 mt-3 mb-4" data-testid="size-guide-box">
                {f.size_guide_image ? (
                  <img src={f.size_guide_image} alt="Guía de medición" className="w-20 h-20 object-cover border border-[#C4C7CD]" />
                ) : (
                  <div className="w-20 h-20 border border-dashed border-[#C4C7CD] flex items-center justify-center text-[10px] text-[#858A91] text-center p-1">imagen guía</div>
                )}
                <div className="space-y-1.5">
                  <button type="button" data-testid="pe-upload-guide" onClick={() => guideRef.current.click()} disabled={uploading} className="btn-outline-ink px-4 py-2 dossier-label inline-flex items-center gap-2"><Upload size={14} /> {f.size_guide_image ? "Cambiar imagen" : "Subir imagen de cómo medir"}</button>
                  {f.size_guide_image && <button type="button" onClick={() => set("size_guide_image", null)} className="dossier-label text-[#565A61] hover:text-[#171719] block">Quitar</button>}
                  <p className="text-[10px] text-[#858A91]">Ej.: una foto o gráfico con "medí tu busto, cintura y cadera con una cinta métrica".</p>
                </div>
                <input ref={guideRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "size_guide_image")} />
              </div>
              <div className="space-y-3">
                {f.sizes.map((s, i) => (
                  <div key={i} className="bg-[#F4F4F3] border border-[#C9CBD1] p-3 space-y-2" data-testid={`size-row-${i}`}>
                    <div className="flex gap-2">
                      <Input className={`${field} mt-0 w-24`} value={s.label} placeholder="Talle" onChange={(e) => { const n = [...f.sizes]; n[i] = { ...n[i], label: e.target.value }; set("sizes", n); }} />
                      <Input className={`${field} mt-0 flex-1`} value={s.measurements} placeholder="Medidas reales (ej: Busto 96 · Largo 58)" onChange={(e) => { const n = [...f.sizes]; n[i] = { ...n[i], measurements: e.target.value }; set("sizes", n); }} />
                      <button onClick={() => set("sizes", f.sizes.filter((_, j) => j !== i))} className="text-[#565A61] hover:text-[#56677A] px-2"><Trash2 size={16} /></button>
                    </div>
                    <Textarea rows={2} className={`${field} mt-0 text-sm`} data-testid={`size-desc-${i}`} value={s.description || ""} placeholder="Descripción del talle (opcional): cómo queda, a quién le va bien, cómo medirlo en casa…" onChange={(e) => { const n = [...f.sizes]; n[i] = { ...n[i], description: e.target.value }; set("sizes", n); }} />
                  </div>
                ))}
                <button data-testid="pe-add-size" onClick={() => set("sizes", [...f.sizes, { label: "", measurements: "", description: "" }])} className="dossier-label text-[#565A61] hover:text-[#171719] inline-flex items-center gap-1"><Plus size={14} /> Agregar talle</button>
              </div>
            </div>

            {/* Images */}
            <div>
              <Label className={lbl}>Imágenes</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {f.images.map((img, i) => (
                  <div key={img} className="w-20" data-testid={`pe-img-${i}`}>
                    <div className="relative w-20 h-24 border border-[#C4C7CD] overflow-hidden group">
                      {i === 0 && <span className="absolute top-1 left-1 bg-[#171719] text-[#F4F4F3] text-[8px] px-1 py-0.5 font-mono2">1ª</span>}
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-1 text-[#F4F4F3]"><X size={12} /></button>
                    </div>
                    <div className="flex justify-between mt-1">
                      <button type="button" data-testid={`pe-img-left-${i}`} disabled={i === 0} onClick={() => { const n = [...f.images]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; set("images", n); }} className="text-[#565A61] hover:text-[#171719] text-xs px-1 disabled:opacity-30" title="Mover a la izquierda">←</button>
                      <button type="button" data-testid={`pe-img-right-${i}`} disabled={i === f.images.length - 1} onClick={() => { const n = [...f.images]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; set("images", n); }} className="text-[#565A61] hover:text-[#171719] text-xs px-1 disabled:opacity-30" title="Mover a la derecha">→</button>
                    </div>
                  </div>
                ))}
                <button data-testid="pe-upload-image" onClick={() => fileRef.current.click()} disabled={uploading} className="w-20 h-24 border border-dashed border-[#C4C7CD] flex flex-col items-center justify-center text-[#565A61] hover:border-[#171719] gap-1">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  <span className="text-[9px]">subir</span>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "images")} />
              </div>
            </div>

            {/* Video */}
            <div>
              <Label className={lbl}>Video opcional</Label>
              <div className="flex items-center gap-3 mt-2">
                {f.video && <video src={f.video} className="w-28 h-20 object-cover border border-[#C4C7CD]" muted />}
                <button onClick={() => videoRef.current.click()} className="btn-outline-ink px-4 py-2 dossier-label inline-flex items-center gap-2"><Upload size={14} /> {f.video ? "Cambiar" : "Subir video"}</button>
                {f.video && <button onClick={() => set("video", null)} className="text-[#565A61] hover:text-[#56677A]"><Trash2 size={16} /></button>}
                <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "video")} />
              </div>
            </div>

            {product?.id && <UnitsManager productId={product.id} designCode={f.design_code} token={token} onWaitlistNotified={() => setWlVersion((v) => v + 1)} />}
            {product?.id && <ProductWaitlist productId={product.id} version={wlVersion} />}
          </div>

          <div className="flex gap-3 p-5 border-t border-[#C9CBD1] sticky bottom-0 bg-[#F4F4F3]">
            <button data-testid="pe-save" onClick={save} disabled={saving} className="btn-ink px-7 py-3 dossier-label disabled:opacity-60">{saving ? "Guardando…" : "Guardar pieza"}</button>
            <button onClick={onClose} className="btn-outline-ink px-7 py-3 dossier-label">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UnitsManager({ productId, designCode, token, onWaitlistNotified }) {
  const [units, setUnits] = useState([]);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/products/${productId}/units`).then((r) => setUnits(r.data)).catch(() => {});
  useEffect(() => { load(); }, [productId]);

  const gen = async (sizeOverride) => {
    const sz = sizeOverride || size;
    if (!sz.trim()) return toast.error("¿Qué talle? Escribí el talle primero");
    setBusy(true);
    try {
      await api.post(`/admin/products/${productId}/units`, { size: sz, quantity: Number(qty) || 1, color: "" });
      toast.success(Number(qty) === 1 ? "Unidad sumada al stock" : "Unidades sumadas al stock");
      load();
      const { data: wl } = await api.get(`/admin/products/${productId}/waitlist`);
      const pending = (wl || []).filter((w) => !w.notified).length;
      if (pending > 0 && window.confirm(`Repusiste stock. Hay ${pending} persona(s) en la lista de espera sin avisar. ¿Les avisamos ahora por email que la pieza volvió?`)) {
        const { data } = await api.post(`/admin/products/${productId}/notify`);
        toast.success(`Avisadas: ${data.sent}`);
        onWaitlistNotified && onWaitlistNotified();
      }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setStatus = async (u, status, msg) => {
    try { await api.put(`/admin/units/${u.id}`, { status, note: "cambio manual" }); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const removeOne = async (u) => {
    try { await api.delete(`/admin/units/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const showQr = async (u) => {
    try { const { data } = await api.get(`/admin/units/${u.id}/qr`); setQr(data); } catch (e) { toast.error(errMsg(e)); }
  };

  const dispo = units.filter((u) => u.status === "disponible");
  const sold = units.filter((u) => u.status === "vendida" || u.status === "reservada");
  const STATUS_LABEL = { disponible: "disponible", vendida: "vendida", reservada: "vendida (reservada)", retirada: "retirada" };
  const sizeRows = [];
  units.forEach((u) => {
    let r = sizeRows.find((x) => x.size === u.size);
    if (!r) { r = { size: u.size, dispo: 0, sold: 0, firstDispo: null }; sizeRows.push(r); }
    if (u.status === "disponible") { r.dispo += 1; if (!r.firstDispo) r.firstDispo = u; }
    else r.sold += 1;
  });

  return (
    <div className="border-t border-[#C9CBD1] pt-5">
      <div className="flex items-center justify-between mb-1">
        <Label className={lbl}>Stock · unidades con código único</Label>
        <a href={`${API}/admin/products/${productId}/units/export?`} onClick={(e) => { e.preventDefault(); downloadCsv(productId, designCode, token); }} className="dossier-label text-[#565A61] hover:text-[#171719] inline-flex items-center gap-1"><Download size={13} /> Exportar CSV</a>
      </div>

      {/* Resumen automático */}
      <div className="flex flex-wrap items-center gap-4 mt-3 mb-5 text-sm" data-testid="stock-summary">
        <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#4C7A51]" /><b>{dispo.length}</b> disponible{dispo.length === 1 ? "" : "s"}</span>
        <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#171719]" /><b>{sold.length}</b> vendida{sold.length === 1 ? "" : "s"}</span>
        <span className="text-[#565A61] text-xs">Se actualiza solo: cada venta marca una unidad como vendida.</span>
      </div>

      {/* Stock por talle con botones + / − */}
      <div className="space-y-2 mb-5" data-testid="stock-by-size">
        {sizeRows.map((r) => (
          <div key={r.size} className="flex items-center gap-3 bg-[#F4F4F3] border border-[#C9CBD1] px-3 py-2">
            <span className="font-display text-lg w-10">T{r.size}</span>
            <span className="text-xs text-[#565A61] flex-1">{r.dispo} disponible{r.dispo === 1 ? "" : "s"} · {r.sold} vendida{r.sold === 1 ? "" : "s"}</span>
            <button data-testid={`stock-plus-${r.size}`} disabled={busy} onClick={() => gen(r.size)} title="Sumar 1 unidad de este talle" className="btn-outline-ink w-8 h-8 text-base leading-none">+</button>
            <button data-testid={`stock-minus-${r.size}`} disabled={busy || r.dispo === 0} onClick={() => removeOne(r.firstDispo)} title="Quitar 1 unidad disponible de este talle" className="btn-outline-ink w-8 h-8 text-base leading-none disabled:opacity-40">−</button>
          </div>
        ))}
        {units.length === 0 && <p className="text-xs text-[#565A61]">Todavía no cargaste stock. Indicá el talle y la cantidad abajo.</p>}
      </div>

      {/* Cargar stock */}
      <div className="flex flex-wrap gap-2 mb-6 items-center">
        <Input data-testid="unit-size" className={`${field} mt-0 w-24`} value={size} onChange={(e) => setSize(e.target.value)} placeholder="Talle" />
        <Input type="number" min="1" data-testid="unit-qty" className={`${field} mt-0 w-24`} value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Cantidad" />
        <button data-testid="generate-units" disabled={busy} onClick={() => gen()} className="btn-ink px-5 py-2.5 dossier-label inline-flex items-center gap-1 disabled:opacity-60"><Plus size={14} /> Cargar al stock</button>
        <span className="text-xs text-[#565A61]">Cada unidad recibe un código único. Se pueden sumar de a varias.</span>
      </div>

      {/* Unidades */}
      <div className="max-h-72 overflow-y-auto no-scrollbar space-y-1.5">
        {units.map((u) => (
          <div key={u.id} className="flex items-center justify-between bg-[#F4F4F3] border border-[#C9CBD1] px-3 py-2">
            <div className="font-mono2 text-xs text-[#2E2E31]">{u.unit_code} <span className="text-[#565A61]">· T{u.size} · Nº{u.edition_number}</span></div>
            <div className="flex items-center gap-2">
              <span className={`dossier-label text-[9px] px-2 py-1 ${u.status === "disponible" ? "bg-[#1C241D] text-[#4C7A51]" : u.status === "retirada" ? "bg-[#171719]/10 text-[#565A61]" : "bg-[#171719] text-[#F4F4F3]"}`} data-testid={`unit-status-${u.id}`}>{STATUS_LABEL[u.status] || u.status}</span>
              {u.status === "disponible" && <button data-testid={`unit-sell-${u.id}`} onClick={() => setStatus(u, "vendida", "Marcada como vendida")} className="btn-outline-ink px-2.5 py-1 dossier-label text-[9px]">Marcar vendida</button>}
              {(u.status === "vendida" || u.status === "retirada") && <button data-testid={`unit-react-${u.id}`} onClick={() => setStatus(u, "disponible", "Unidad nuevamente disponible")} className="btn-outline-ink px-2.5 py-1 dossier-label text-[9px]">Poner disponible</button>}
              {u.status === "reservada" && <button onClick={() => setStatus(u, "disponible", "Reserva liberada")} className="btn-outline-ink px-2.5 py-1 dossier-label text-[9px]">Liberar reserva</button>}
              {u.status === "disponible" && <button onClick={() => removeOne(u)} title="Quitar unidad" className="text-[#565A61] hover:text-[#171719]"><Trash2 size={14} /></button>}
              <button onClick={() => showQr(u)} title="Ver QR" className="text-[#565A61] hover:text-[#171719]"><QrCode size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {qr && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setQr(null)}>
          <div className="bg-white p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <img src={qr.qr} alt="QR" className="w-56 h-56 mx-auto" />
            <div className="font-mono2 text-xs mt-3">{qr.unit_code}</div>
            <button onClick={() => setQr(null)} className="btn-outline-ink px-4 py-2 dossier-label mt-3">Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductWaitlist({ productId, version }) {
  const [wl, setWl] = useState([]);
  const load = () => api.get(`/admin/products/${productId}/waitlist`).then((r) => setWl(r.data)).catch(() => {});
  useEffect(() => { load(); }, [productId, version]);
  const pending = wl.filter((w) => !w.notified).length;
  const notify = async () => {
    if (!window.confirm(`¿Avisar por email a ${pending} persona(s) que todavía no contactaste que la pieza volvió?`)) return;
    try { const { data } = await api.post(`/admin/products/${productId}/notify`); toast.success(`Emails enviados: ${data.sent}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border-t border-[#C9CBD1] pt-5">
      <div className="flex items-center justify-between mb-3">
        <Label className={lbl}>Lista de espera · "avisame si vuelve"</Label>
        {pending > 0
          ? <button data-testid="notify-product" onClick={notify} className="dossier-label text-[#171719] hover:text-[#56677A]">Avisar que volvió ({pending})</button>
          : wl.length > 0 && <span className="dossier-label text-[#4C7A51]">todas avisadas</span>}
      </div>
      {wl.length === 0 ? <p className="text-xs text-[#565A61]">Nadie anotado todavía.</p> : (
        <div className="max-h-40 overflow-y-auto no-scrollbar space-y-1.5">
          {wl.map((w) => (
            <div key={w.id} className="flex items-center justify-between bg-[#F4F4F3] border border-[#C9CBD1] px-3 py-2 text-xs">
              <span className="text-[#2E2E31]">{w.name} · {w.email}{w.size ? ` · talle ${w.size}` : ""}</span>
              {w.notified && <span className="dossier-label text-[#4C7A51]">avisada</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function downloadCsv(productId, designCode, token) {
  try {
    const res = await fetch(`${API}/admin/products/${productId}/units/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `unidades_${designCode}.csv`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("No se pudo exportar"); }
}
